//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { IExperienceCustomizationsDto } from '@apis/experiences/experiencesApiModels';
import { CopyCustomizationSections } from '@galleries/constants/galleriesConstants';
import {
  DEFAULT_FONT_VALUE,
  DEFAULT_GUTTER_SIZE,
} from '@galleries/constants/previewConstants';
import {
  FanreelCustomizationSortValues,
  HoverThemeColors,
} from '@legacy-components/fanreel-self-serve-customization/fanreel-customization-types';

import * as DefaultLocaleStrings from 'app/constants/locales/defaultLocaleStrings';
import { Locales } from 'app/constants/locales/locale';

export type CustomizationOptions = Partial<IExperienceCustomizationsDto> &
  Record<string, unknown>;

export const LAYOUT_DESIGN_KEYS = [
  'carouselHoverShouldRoundLikes',
  'carouselHoverShowCta',
  'carouselHoverShowInstagramIcon',
  'carouselHoverShowLikes',
  'carouselHoverShowPlusIcon',
  'carouselHoverShowUser',
  'carouselNavigationArrowStyle',
  'carouselNavigationBackgroundColor',
  'carouselNavigationBorderColor',
  'carouselNavigationButtonStyle',
  'carouselNavigationIconColor',
  'carouselNavigationIconStyle',
  'carouselNavigationPosition',
  'carouselThumbnailImageAspectRatio',
  'carouselThumbnailImageIsSquare',
  'carouselThumbnailShowCaption',
  'carouselThumbnailShowVideoIcon',
  'font',
  'galleryHoverShouldRoundLikes',
  'galleryHoverShowCta',
  'galleryHoverShowInstagramIcon',
  'galleryHoverShowLikes',
  'galleryHoverShowPlusIcon',
  'galleryHoverShowUser',
  'galleryLinkCta',
  'galleryLinkUrl',
  'galleryThumbnailImageAspectRatio',
  'galleryThumbnailImageIsSquare',
  'galleryThumbnailShowVideoIcon',
  'gutterSize',
  'headerCtaPosition',
  'headerFontSize',
  'headerGalleryLinkCtaBackgroundColor',
  'headerGalleryLinkCtaBorderColor',
  'headerGalleryLinkCtaButtonStyle',
  'headerGalleryLinkCtaColor',
  'headerGalleryLinkCtaShape',
  'headerGalleryLinkCtaStyle',
  'headerGalleryLinkCtaTextColor',
  'headerSubtext',
  'headerText',
  'headerTextColor',
  'hoverCtaBackgroundColor',
  'hoverCtaBorderColor',
  'hoverCtaButtonStyle',
  'hoverCtaColor',
  'hoverCtaShape',
  'hoverCtaStyle',
  'hoverCtaTextColor',
  'hoverOverlayColor',
  'hoverStateColor',
  'hoverText',
  'hoverTextColor',
  'layout',
  'loadMoreBackgroundColor',
  'loadMoreBorderColor',
  'loadMoreButtonStyle',
  'loadMoreCtaColor',
  'loadMoreCtaShape',
  'loadMoreCtaStyle',
  'loadMoreCtaText',
  'loadMoreTextColor',
  'minNumberOfColumns',
  'numberOfColumns',
  'numberOfRows',
  'pagination',
  'showGalleryLink',
  'showHeader',
  'showSubtitle',
  'showTitle',
  'sort',
  'subheaderFontSize',
  'subheaderTextColor',
] as const;

export const MODAL_STYLE_KEYS = [
  'ctaButtonColor',
  'ctaText',
  'ctaTextColor',
  'ecommercePlatformType',
  'ecommerceProductDetailsCta',
  'enableReportMedia',
  'iconColor',
  'includeEcommerceMetadata',
  'includeSocialMetadata',
  'itemTrayTitle',
  'modalAddToCartBackgroundColor',
  'modalAddToCartBorderColor',
  'modalAddToCartButtonStyle',
  'modalAddToCartButtonText',
  'modalAddToCartCtaColor',
  'modalAddToCartCtaShape',
  'modalAddToCartCtaStyle',
  'modalAddToCartIsEnabled',
  'modalAddToCartTextColor',
  'modalAddToCartViewMoreBackgroundColor',
  'modalAddToCartViewMoreBorderColor',
  'modalAddToCartViewMoreButtonStyle',
  'modalAddToCartViewMoreButtonText',
  'modalAddToCartViewMoreCtaColor',
  'modalAddToCartViewMoreCtaShape',
  'modalAddToCartViewMoreCtaStyle',
  'modalAddToCartViewMoreTextColor',
  'modalBackgroundColor',
  'modalBvLogoColor',
  'modalEcommerceProductPriceColor',
  'modalEcommerceProductSalePriceColor',
  'modalEcommerceShopButtonColor',
  'modalEcommerceShopButtonTextColor',
  'modalEcommerceTitleColor',
  'modalHeroIsSquare',
  'modalHeroProgressBarInProgressBackground',
  'modalHeroVideoAutoplay',
  'modalImageAspectRatio',
  'modalScrollHintTextColor',
  'modalTextColor',
  'modalType',
  'platformType',
  'primaryButtonColor',
  'primaryButtonShape',
  'primaryButtonStyle',
  'primaryButtonText',
  'primaryButtonTextColor',
  'productDisplayStyle',
  'salesforceLocale',
  'salesforceSiteId',
  'secondaryButtonColor',
  'secondaryButtonShape',
  'secondaryButtonStyle',
  'secondaryButtonText',
  'secondaryButtonTextColor',
  'showAddToCart',
  'showBrandName',
  'showCaption',
  'showLikeCount',
  'showProductTags',
  'showReportMedia',
  'showShareIcons',
  'showTimestamp',
  'showUser',
] as const;

export const UPLOADER_KEYS = [
  'enableUploader',
  'headerUploaderCtaBackgroundColor',
  'headerUploaderCtaBorderColor',
  'headerUploaderCtaButtonStyle',
  'headerUploaderCtaColor',
  'headerUploaderCtaShape',
  'headerUploaderCtaStyle',
  'headerUploaderCtaTextColor',
  'showUploaderCta',
  'uploaderCtaText',
  'uploaderTermsAndConditionsLink',
] as const;

export const LOCALIZATION_KEYS = ['locale', 'localizationStrings'] as const;

type ListedCopySectionKey =
  | (typeof LAYOUT_DESIGN_KEYS)[number]
  | (typeof MODAL_STYLE_KEYS)[number]
  | (typeof UPLOADER_KEYS)[number]
  | (typeof LOCALIZATION_KEYS)[number];

type ExcludedCopySectionKey =
  | 'filterLabelSet'
  | 'reportMediaReelId'
  | 'uploaderReelId';

type UncategorizedCustomizationKey = Exclude<
  keyof IExperienceCustomizationsDto,
  ListedCopySectionKey | ExcludedCopySectionKey
>;

export const copyCustomizationKeyCoverage: UncategorizedCustomizationKey extends never
  ? true
  : { missingKeys: UncategorizedCustomizationKey } = true;

const LAYOUT_DESIGN_LOCALIZED_STRING_KEYS: readonly string[] = [
  'galleryLinkCta',
  'galleryLinkUrl',
  'headerSubtext',
  'headerText',
  'hoverText',
  'loadMoreCtaText',
];

const MODAL_STYLE_LOCALIZED_STRING_KEYS: readonly string[] = [
  'ctaText',
  'ecommerceCta',
  'ecommerceOutOfStockCta',
  'ecommerceProductDetailsCta',
  'ecommerceTitle',
  'itemTrayTitle',
  'primaryButtonText',
  'secondaryButtonText',
];

const UPLOADER_LOCALIZED_STRING_KEYS: readonly string[] = [
  'uploaderCtaText',
  'uploaderTermsAndConditionsLink',
];

export interface CopyCustomizationConstraints {
  isLocalizationEnabled: boolean;
  isSourceInstaShop: boolean;
  isTargetInstaShop: boolean;
}

// Keys absent from a preview/update request are backfilled by the backend
// from the target's persisted customization, which would resurrect the old
// values. Reset them to explicit defaults instead of deleting.
export const SECTION_KEY_DEFAULTS: Record<string, unknown> = {
  ...DefaultLocaleStrings.localizationStrings[Locales.EN_US],
  carouselNavigationBackgroundColor: HoverThemeColors.WHITE,
  carouselNavigationBorderColor: HoverThemeColors.BLACK,
  carouselNavigationIconColor: HoverThemeColors.BLACK,
  ctaButtonColor: HoverThemeColors.BLACK,
  ctaText: 'Shop Now',
  ctaTextColor: HoverThemeColors.WHITE,
  enableUploader: false,
  font: DEFAULT_FONT_VALUE,
  gutterSize: DEFAULT_GUTTER_SIZE,
  iconColor: HoverThemeColors.BLACK,
  modalBackgroundColor: HoverThemeColors.BLACK,
  modalTextColor: '#7E7E7E',
  showAddToCart: false,
  showUploaderCta: false,
};

function replaceSectionKeysFromSource(
  targetCustomizations: CustomizationOptions,
  sourceCustomizations: Record<string, unknown>,
  sectionKeys: readonly string[],
): void {
  for (const sectionKey of sectionKeys) {
    const sourceValue = sourceCustomizations[sectionKey];
    if (sourceValue !== undefined) {
      targetCustomizations[sectionKey] = sourceValue;
    } else if (sectionKey in SECTION_KEY_DEFAULTS) {
      targetCustomizations[sectionKey] = SECTION_KEY_DEFAULTS[sectionKey];
    } else {
      Reflect.deleteProperty(targetCustomizations, sectionKey);
    }
  }
}

function getEnUsStrings(
  customizations: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const localizationStringsByLocale = customizations.localizationStrings as
    | Record<string, Record<string, unknown>>
    | undefined;
  return localizationStringsByLocale?.[Locales.EN_US];
}

function syncLocalizedKeys(
  mergedCustomizations: CustomizationOptions,
  sourceCustomizations: Record<string, unknown>,
  localizedStringKeys: readonly string[],
): void {
  const sourceEnUsStrings = getEnUsStrings(sourceCustomizations);
  for (const localizedStringKey of localizedStringKeys) {
    if (
      sourceCustomizations[localizedStringKey] === undefined &&
      sourceEnUsStrings?.[localizedStringKey] !== undefined
    ) {
      mergedCustomizations[localizedStringKey] =
        sourceEnUsStrings[localizedStringKey];
    }
  }

  const localizationStringsByLocale =
    (mergedCustomizations.localizationStrings as
      | Record<string, Record<string, unknown>>
      | undefined) ?? {};

  const enUsStrings = { ...localizationStringsByLocale[Locales.EN_US] };
  for (const localizedStringKey of localizedStringKeys) {
    const flatValue = mergedCustomizations[localizedStringKey];
    if (flatValue === undefined) {
      Reflect.deleteProperty(enUsStrings, localizedStringKey);
    } else {
      enUsStrings[localizedStringKey] = flatValue;
    }
  }
  mergedCustomizations.localizationStrings = {
    ...localizationStringsByLocale,
    [Locales.EN_US]: enUsStrings,
  };
}

function applyLayoutDesignSection(
  mergedCustomizations: CustomizationOptions,
  sourceCustomizations: Record<string, unknown>,
  constraints: CopyCustomizationConstraints,
): readonly string[] {
  replaceSectionKeysFromSource(
    mergedCustomizations,
    sourceCustomizations,
    LAYOUT_DESIGN_KEYS,
  );
  const mergedRecord = mergedCustomizations as Record<string, unknown>;
  if (
    constraints.isTargetInstaShop &&
    mergedRecord.sort === FanreelCustomizationSortValues.MODERATION
  ) {
    mergedRecord.sort = FanreelCustomizationSortValues.LATEST;
  }
  return LAYOUT_DESIGN_LOCALIZED_STRING_KEYS;
}

function applyModalStyleSection(
  mergedCustomizations: CustomizationOptions,
  sourceCustomizations: Record<string, unknown>,
): readonly string[] {
  replaceSectionKeysFromSource(
    mergedCustomizations,
    sourceCustomizations,
    MODAL_STYLE_KEYS,
  );
  return MODAL_STYLE_LOCALIZED_STRING_KEYS;
}

function applyUploaderSection(
  mergedCustomizations: CustomizationOptions,
  sourceCustomizations: Record<string, unknown>,
): readonly string[] {
  replaceSectionKeysFromSource(
    mergedCustomizations,
    sourceCustomizations,
    UPLOADER_KEYS,
  );
  return UPLOADER_LOCALIZED_STRING_KEYS;
}

function applyLocalizationSection(
  mergedCustomizations: CustomizationOptions,
  sourceCustomizations: Record<string, unknown>,
): void {
  replaceSectionKeysFromSource(
    mergedCustomizations,
    sourceCustomizations,
    LOCALIZATION_KEYS,
  );
  if (mergedCustomizations.localizationStrings !== undefined) {
    mergedCustomizations.localizationStrings = structuredClone(
      mergedCustomizations.localizationStrings,
    );
  }
}

export function copySectionCustomization(
  sourceCustomizationsDto: IExperienceCustomizationsDto | CustomizationOptions,
  targetCustomizations: CustomizationOptions,
  selectedSections: CopyCustomizationSections,
  constraints: CopyCustomizationConstraints,
): CustomizationOptions {
  const sourceCustomizations = sourceCustomizationsDto as unknown as Record<
    string,
    unknown
  >;
  const mergedCustomizations: CustomizationOptions = {
    ...targetCustomizations,
  };

  const localizedStringKeysToSync: string[] = [];

  if (selectedSections.layoutDesign) {
    localizedStringKeysToSync.push(
      ...applyLayoutDesignSection(
        mergedCustomizations,
        sourceCustomizations,
        constraints,
      ),
    );
  }
  if (selectedSections.modalStyle) {
    localizedStringKeysToSync.push(
      ...applyModalStyleSection(mergedCustomizations, sourceCustomizations),
    );
  }
  if (
    selectedSections.uploader &&
    !constraints.isSourceInstaShop &&
    !constraints.isTargetInstaShop
  ) {
    localizedStringKeysToSync.push(
      ...applyUploaderSection(mergedCustomizations, sourceCustomizations),
    );
  }
  if (selectedSections.localization && constraints.isLocalizationEnabled) {
    applyLocalizationSection(mergedCustomizations, sourceCustomizations);
  }

  if (localizedStringKeysToSync.length > 0) {
    syncLocalizedKeys(
      mergedCustomizations,
      sourceCustomizations,
      localizedStringKeysToSync,
    );
  }
  return mergedCustomizations;
}
